import { useState, type FormEvent } from "react";
import { postForm } from "@/utils/form";

const SUBMIT_URL = "https://readdy.ai/api/form/db74hjbaskivmpnrcfq0";

const initialState = { name: "", email: "", phone: "", subject: "影片咨询", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const update = (key: keyof typeof initialState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setFeedback("请填写姓名、邮箱与留言内容");
      return;
    }
    setStatus("submitting");
    setFeedback("");
    try {
      const result = await postForm(SUBMIT_URL, formEl, "website_alt");
      if (result.ok) {
        setStatus("success");
        setFeedback(result.message || "留言已提交成功，感谢你的反馈");
        setForm(initialState);
      } else {
        setStatus("error");
        setFeedback(result.message);
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <form
      id="contactForm"
      data-readdy-form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70"
    >
      <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">在线留言</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="ct-name" className="block text-xs text-foreground-500 mb-1.5">
            姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            id="ct-name"
            name="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="请输入姓名"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
          />
        </div>
        <div>
          <label htmlFor="ct-email" className="block text-xs text-foreground-500 mb-1.5">
            邮箱 <span className="text-accent-500">*</span>
          </label>
          <input
            id="ct-email"
            name="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
          />
        </div>
        <div>
          <label htmlFor="ct-phone" className="block text-xs text-foreground-500 mb-1.5">
            联系电话
          </label>
          <input
            id="ct-phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="选填"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
          />
        </div>
        <div>
          <label htmlFor="ct-subject" className="block text-xs text-foreground-500 mb-1.5">
            留言类型
          </label>
          <select
            id="ct-subject"
            name="subject"
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-accent-500/70 cursor-pointer"
          >
            {["影片咨询", "场次预约", "团体包场", "合作洽谈", "意见反馈"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ct-message" className="block text-xs text-foreground-500 mb-1.5">
            留言内容 <span className="text-accent-500">*</span>（最多 500 字）
          </label>
          <textarea
            id="ct-message"
            name="message"
            value={form.message}
            maxLength={500}
            required
            rows={4}
            onChange={(e) => update("message", e.target.value)}
            placeholder="请描述你的问题或建议"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70 resize-none"
          />
        </div>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-extra-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {feedback && (
        <p
          className={`mt-4 text-sm ${status === "success" ? "text-accent-500" : "text-primary-700"}`}
        >
          <i className={status === "success" ? "ri-checkbox-circle-line mr-1" : "ri-error-warning-line mr-1"} />
          {feedback}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 font-label font-medium hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交留言
          </>
        )}
      </button>
    </form>
  );
}